import { queryOptions, useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { type TriggerRecord } from "../TriggerRecord";
import { type TriggerService } from "../TriggerService";

import { triggersQueryKeys } from "./triggersQueryKeys";

/**
 * The query that reads one trigger by id, shared by {@link useTrigger}
 * and the trigger routes' loader.
 */
export function triggerQueryOptions(
  triggerId: string | undefined,
  triggerService: TriggerService,
) {
  return queryOptions<TriggerRecord, Error>({
    queryKey: triggersQueryKeys.byId(triggerId ?? ""),
    queryFn: ({ signal }) => {
      if (triggerId === undefined) {
        return Promise.reject(new Error("Invalid trigger id"));
      }
      return triggerService.getTriggerById(triggerId, {
        abortSignal: signal,
      });
    },
    enabled: triggerId !== undefined,
    // A failed read is not sent again each time a trigger page mounts
    // on it.
    retryOnMount: false,
  });
}

/** One trigger by id, in the unified shape. See {@link triggerQueryOptions}. */
export function useTrigger(triggerId: string | undefined) {
  const { triggerService } = useDomainServices();

  return useQuery(triggerQueryOptions(triggerId, triggerService));
}
